'use client'

import { Disclaimer, PlainButton, Span, type ITravelerInfo } from '@pcln/horizon'
import { AiSearchInput } from '@pcln/horizon-search-form'

import {
  AI_SEARCH_ANIMATED_PLACEHOLDERS,
  AI_SEARCH_HELPER_TEXT,
  AI_SEARCH_INPUT_LABEL,
  AI_SEARCH_MIN_QUERY_LENGTH,
  AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK,
  AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL,
  AI_SEARCH_PLACEHOLDER,
  AI_SEARCH_RESOLVE_ERROR_BEFORE_LINK,
} from './aiSearchConstants'
import AiSearchFilterChips from './AiSearchFilterChips'
import AiSearchSuggestions from './AiSearchSuggestions'
import useAiSearchForm, { type AiSearchSubmitParams } from './useAiSearchForm'

export type { AiSearchSubmitParams }

type AiSearchFormProps = {
  isMobile?: boolean
  initialStartDate?: string
  initialEndDate?: string
  initialTravelers?: ITravelerInfo
  onSubmitQuery: (params: AiSearchSubmitParams) => Promise<void> | void
  /** Called with the query that returned no recommendations when the user opts into Standard Search. */
  onSwitchToStandardSearch?: (searchQuery: string) => void
}

export default function AiSearchForm({
  isMobile = false,
  initialStartDate,
  initialEndDate,
  initialTravelers,
  onSubmitQuery,
  onSwitchToStandardSearch,
}: Readonly<AiSearchFormProps>) {
  const {
    inputValue,
    startDate,
    endDate,
    travelers,
    isSubmitting,
    selectedChipKey,
    hasSubmitError,
    hasQuery,
    inputId,
    helperTextId,
    submitErrorId,
    recommendationsLabelId,
    recommendationsStatusId,
    emptyRecommendationsId,
    errorRecommendationsId,
    inputDescribedBy,
    fetchQuery,
    suggestionChips,
    hasTypedQuery,
    status,
    isSubmitDisabledBySuggestions,
    setTravelers,
    handleFormSubmit,
    handleInputSubmit,
    handleInputChange,
    handleDatesChange,
    handleChipSelect,
  } = useAiSearchForm({
    initialStartDate,
    initialEndDate,
    initialTravelers,
    onSubmitQuery,
  })

  return (
    <div className='flex w-full flex-col gap-3'>
      <form
        className='flex w-full flex-col gap-3'
        aria-label='AI search form'
        aria-busy={isSubmitting}
        noValidate
        onSubmit={handleFormSubmit}
      >
        <AiSearchInput
          id={inputId}
          value={inputValue}
          onChange={handleInputChange}
          onSubmit={handleInputSubmit}
          placeholder={AI_SEARCH_PLACEHOLDER}
          animatedPlaceholders={[...AI_SEARCH_ANIMATED_PLACEHOLDERS]}
          aria-label={AI_SEARCH_INPUT_LABEL}
          aria-describedby={inputDescribedBy}
          disabled={isSubmitting}
          minSubmitLength={AI_SEARCH_MIN_QUERY_LENGTH}
          submitDisabled={isSubmitDisabledBySuggestions}
          className='w-full'
        />
      </form>

      {hasQuery ? (
        <AiSearchFilterChips
          startDate={startDate}
          endDate={endDate}
          travelers={travelers}
          onDatesChange={handleDatesChange}
          onTravelersChange={setTravelers}
        />
      ) : null}

      <Span id={helperTextId} textStyle='body2' palette='neutral' shade='10'>
        {AI_SEARCH_HELPER_TEXT}
      </Span>

      {hasSubmitError ? (
        <div id={submitErrorId} role='alert' data-testid='ai-search-submit-error'>
          <Disclaimer className='px-0 py-0'>
            {AI_SEARCH_RESOLVE_ERROR_BEFORE_LINK}
            {onSwitchToStandardSearch ? (
              <PlainButton
                onClick={() => onSwitchToStandardSearch(inputValue.trim())}
                type='primary'
                variant='inline'
              >
                {AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL}
              </PlainButton>
            ) : (
              AI_SEARCH_NO_RECOMMENDATIONS_LINK_LABEL
            )}
            {AI_SEARCH_NO_RECOMMENDATIONS_AFTER_LINK}
          </Disclaimer>
        </div>
      ) : null}

      <AiSearchSuggestions
        isMobile={isMobile}
        status={status}
        suggestionChips={suggestionChips}
        selectedChipKey={selectedChipKey}
        hasTypedQuery={hasTypedQuery}
        fetchQuery={fetchQuery}
        statusId={recommendationsStatusId}
        labelId={recommendationsLabelId}
        emptyId={emptyRecommendationsId}
        errorId={errorRecommendationsId}
        onChipSelect={handleChipSelect}
        onSwitchToStandardSearch={onSwitchToStandardSearch}
      />
    </div>
  )
}
